import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, CheckCircle, Loader2 } from 'lucide-react';
import { companyInfo, bookingFormFields } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitError('');

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit enquiry');
      }

      const data = await response.json();
      console.log('Enquiry submitted:', data);
      
      setIsSubmitted(true);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      }, 5000);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError('Failed to submit your enquiry. Please try again or call us directly.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-construction-darkblue text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Get in touch with our expert team - we're here to help you get the qualifications you need
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-construction-darkblue mb-6">
                  Get In Touch
                </h2>
                <p className="text-gray-600 mb-6">
                  Have questions? We're here to help! Contact us using any of the methods below.
                </p>
              </div>

              {/* Phone */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-construction-yellow/20 p-2 rounded-lg">
                    <Phone className="text-construction-yellow" size={24} />
                  </div>
                  <h3 className="font-bold text-construction-darkblue">Phone</h3>
                </div>
                <a 
                  href={`tel:${companyInfo.phone}`}
                  className="text-lg font-semibold text-construction-yellow hover:text-yellow-600 transition-colors"
                >
                  {companyInfo.phone}
                </a>
                <p className="text-sm text-gray-600 mt-1">Mon-Fri: 8AM-6PM</p>
              </div>

              {/* WhatsApp */}
              <div className="bg-green-50 p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-green-500/20 p-2 rounded-lg">
                    <MessageCircle className="text-green-600" size={24} />
                  </div>
                  <h3 className="font-bold text-construction-darkblue">WhatsApp</h3>
                </div>
                <a 
                  href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-semibold text-green-600 hover:text-green-700 transition-colors"
                >
                  {companyInfo.whatsapp}
                </a>
                <p className="text-sm text-gray-600 mt-1">Fast response</p>
              </div>

              {/* Email */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-construction-yellow/20 p-2 rounded-lg">
                    <Mail className="text-construction-yellow" size={24} />
                  </div>
                  <h3 className="font-bold text-construction-darkblue">Email</h3>
                </div>
                <a 
                  href={`mailto:${companyInfo.email}`}
                  className="text-sm font-semibold text-construction-yellow hover:text-yellow-600 transition-colors break-all"
                >
                  {companyInfo.email}
                </a>
                <p className="text-sm text-gray-600 mt-1">24-48hr response</p>
              </div>

              {/* Address */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-construction-yellow/20 p-2 rounded-lg">
                    <MapPin className="text-construction-yellow" size={24} />
                  </div>
                  <h3 className="font-bold text-construction-darkblue">Address</h3>
                </div>
                <p className="text-gray-700">
                  {companyInfo.address}
                </p>
              </div>

              {/* Hours */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-construction-yellow/20 p-2 rounded-lg">
                    <Clock className="text-construction-yellow" size={24} />
                  </div>
                  <h3 className="font-bold text-construction-darkblue">Business Hours</h3>
                </div>
                <p className="text-gray-700 text-sm whitespace-pre-line">
                  {companyInfo.businessHours}
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 p-8 rounded-xl">
                <h2 className="text-2xl font-bold text-construction-darkblue mb-6">
                  Send Us a Message
                </h2>

                {isSubmitted ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
                    <CheckCircle className="text-green-600 mx-auto mb-4" size={48} />
                    <h3 className="text-xl font-bold text-green-700 mb-2">
                      Message Sent Successfully!
                    </h3>
                    <p className="text-gray-700">
                      Thank you for contacting us. We'll get back to you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-construction-darkblue mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow"
                          placeholder="John Smith"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-construction-darkblue mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow"
                          placeholder="07XXX XXXXXX"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-construction-darkblue mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow"
                        placeholder="john.smith@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-construction-darkblue mb-2">
                        Service Required *
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow bg-white"
                      >
                        <option value="">Select a service...</option>
                        {bookingFormFields.services.map((service, index) => (
                          <option key={index} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-construction-darkblue mb-2">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="5"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow resize-none"
                        placeholder="Please provide details about your requirements..."
                      ></textarea>
                    </div>

                    {/* Submit Error */}
                    {submitError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        {submitError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      data-testid="submit-contact-btn"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          Send Message
                        </>
                      )}
                    </button>

                    <p className="text-sm text-gray-600 text-center">
                      By submitting this form, you agree to our privacy policy and terms of service.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-construction-darkblue mb-8 text-center">
            Find Us
          </h2>
          <div className="max-w-5xl mx-auto bg-white rounded-xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2482.0635953715947!2d-0.0886!3d51.5274!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761ca6d2f3c3db%3A0xb4a0e9fbe5b0e33b!2s124%20City%20Rd%2C%20London%20EC1V%202NX%2C%20UK!5e0!3m2!1sen!2s!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Construction Cards Helpline Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
